import React, {useState, useEffect} from 'react';
import {LoginCredentials, RegisterCredentials} from '../../hooks/useAuth';
import './AuthModal.css';

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
    onLogin: (credentials: LoginCredentials) => Promise<void>;
    onRegister: (credentials: RegisterCredentials) => Promise<void>;
    isLoading: boolean;
    error: string | null;
    message: string | null;
    onClearMessage?: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({
                                                 isOpen,
                                                 onClose,
                                                 onLogin,
                                                 onRegister,
                                                 isLoading,
                                                 error,
                                                 message,
                                                 onClearMessage
                                             }) => {
    const [isLoginMode, setIsLoginMode] = useState(true);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
    });

    // Réinitialiser le formulaire quand la modal s'ouvre
    useEffect(() => {
        if (isOpen) {
            setFormData({
                username: '',
                email: '',
                password: '',
                confirmPassword: ''
            });
        }
    }, [isOpen]);

    // Effacer le message quand on change de mode
    useEffect(() => {
        if (onClearMessage) {
            onClearMessage();
        }
    }, [isLoginMode, onClearMessage]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            if (isLoginMode) {
                await onLogin({
                    username: formData.username,
                    password: formData.password
                });
            } else {
                if (formData.password !== formData.confirmPassword) {
                    return;
                }
                await onRegister({
                    username: formData.username,
                    email: formData.email,
                    password: formData.password
                });
            }
            // Fermer la modal après une authentification réussie
            onClose();
        } catch (error) {
            // Les erreurs sont gérées par le hook useAuth
            console.error('Erreur lors de l\'authentification:', error);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const resetForm = () => {
        setFormData({
            username: '',
            email: '',
            password: '',
            confirmPassword: ''
        });
    };

    const switchMode = () => {
        setIsLoginMode(!isLoginMode);
        resetForm();
    };

    return (
        <div className="auth-modal-overlay" onClick={onClose}>
            <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
                <div className="auth-modal-header">
                    <h2>{isLoginMode ? 'Se connecter' : 'Créer un compte'}</h2>
                    <button className="auth-modal-close" onClick={onClose}>×</button>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                    <div className="form-group">
                        <label htmlFor="username">Nom d&#39;utilisateur</label>
                        <input
                            type="text"
                            id="username"
                            name="username"
                            value={formData.username}
                            onChange={handleInputChange}
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {!isLoginMode && (
                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                disabled={isLoading}
                            />
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="password">Mot de passe</label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {!isLoginMode && (
                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                required
                                disabled={isLoading}
                            />
                            {formData.password !== formData.confirmPassword && formData.confirmPassword && (
                                <span className="error-text">Les mots de passe ne correspondent pas</span>
                            )}
                        </div>
                    )}

                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    {message && (
                        <div className="success-message">
                            {message}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="auth-submit-btn"
                        disabled={isLoading || (!isLoginMode && formData.password !== formData.confirmPassword)}
                    >
                        {isLoading ? 'Chargement...' : (isLoginMode ? 'Se connecter' : 'Créer le compte')}
                    </button>
                </form>

                <div className="auth-switch">
                    <p>
                        {isLoginMode ? "Vous n'avez pas de compte ?" : "Vous avez déjà un compte ?"}
                        <button type="button" onClick={switchMode} className="auth-switch-btn">
                            {isLoginMode ? 'Créer un compte' : 'Se connecter'}
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AuthModal;
